// src/pages/admin/JobHistory.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  alpha,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Star as StarIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  Schedule as ScheduledIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

// Mock data for job history
const generateJobHistory = () => {
  const jobs = [];
  const statuses = ['completed', 'cancelled', 'completed', 'completed', 'cancelled'];
  const services = ['Standard Cleaning', 'Deep Cleaning', 'Office Cleaning', 'Move-In Cleaning', 'Move-Out Cleaning'];
  const customers = ['Sarah Johnson', 'Mike Brown', 'Emily Davis', 'David Wilson', 'Jennifer Lopez'];
  const cleaners = ['Maria Garcia', 'James Wilson', 'Carlos Rodriguez', 'Lisa Chen', 'Robert Smith'];

  for (let i = 1; i <= 50; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const customer = customers[Math.floor(Math.random() * customers.length)];
    const cleaner = cleaners[Math.floor(Math.random() * cleaners.length)];
    
    jobs.push({
      id: i,
      customer,
      cleaner,
      service,
      date: new Date(2024, 2, Math.floor(Math.random() * 28) + 1),
      duration: `${Math.floor(Math.random() * 4) + 2} hours`,
      amount: Math.floor(Math.random() * 200) + 50,
      status,
      rating: status === 'completed' ? Math.floor(Math.random() * 2) + 4 : null,
      review: status === 'completed' && Math.random() > 0.3 ? 'Great service, very thorough!' : null,
      address: `${Math.floor(Math.random() * 1000) + 1} Main St, City, State`,
      paymentStatus: Math.random() > 0.2 ? 'paid' : 'pending'
    });
  }

  return jobs.sort((a, b) => b.date - a.date);
};

const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'success', label: 'Completed', icon: <CompletedIcon /> };
      case 'cancelled':
        return { color: 'error', label: 'Cancelled', icon: <CancelledIcon /> };
      case 'scheduled':
        return { color: 'info', label: 'Scheduled', icon: <ScheduledIcon /> };
      case 'pending':
        return { color: 'warning', label: 'Pending', icon: <PendingIcon /> };
      default:
        return { color: 'default', label: status, icon: null };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      icon={config.icon}
    />
  );
};

const PaymentChip = ({ status }) => {
  const getPaymentConfig = (status) => {
    switch (status) {
      case 'paid':
        return { color: 'success', label: 'Paid' };
      case 'pending':
        return { color: 'warning', label: 'Pending' };
      case 'refunded':
        return { color: 'error', label: 'Refunded' };
      default:
        return { color: 'default', label: status };
    }
  };

  const config = getPaymentConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

export default function JobHistory() {
  const theme = useTheme();
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  useEffect(() => {
    setJobs(generateJobHistory());
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.cleaner.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
    const matchesPayment = filterPayment === 'all' || job.paymentStatus === filterPayment;

    return matchesSearch && matchesStatus && matchesPayment;
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedJobs(filteredJobs.map(job => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedJobs.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedJobs, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedJobs.slice(1));
    } else if (selectedIndex === selectedJobs.length - 1) {
      newSelected = newSelected.concat(selectedJobs.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedJobs.slice(0, selectedIndex),
        selectedJobs.slice(selectedIndex + 1),
      );
    }

    setSelectedJobs(newSelected);
  };

  const isSelected = (id) => selectedJobs.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionMenuOpen = (event, job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
    setSelectedJob(null);
  };

  const handleViewReview = (job) => {
    setSelectedJob(job);
    setReviewDialogOpen(true);
  };

  const stats = [
    { label: 'Total Jobs', value: jobs.length, change: '+12%' },
    { label: 'Completed', value: jobs.filter(j => j.status === 'completed').length, change: '+8%' },
    { label: 'Cancelled', value: jobs.filter(j => j.status === 'cancelled').length, change: '-2%' },
    { label: 'Avg. Rating', value: '4.7/5', change: '+0.1' },
  ];

  const totalRevenue = jobs
    .filter(job => job.status === 'completed')
    .reduce((sum, job) => sum + job.amount, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Job History
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export Report
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View complete history of all cleaning jobs and their details
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                {jobs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Jobs
              </Typography>
              <Chip 
                label="+12%" 
                size="small" 
                color="success"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                ${totalRevenue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Revenue
              </Typography>
              <Chip 
                label="+15%" 
                size="small" 
                color="success"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="info.main" gutterBottom>
                {jobs.filter(j => j.status === 'completed').length}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Completed Jobs
              </Typography>
              <Chip 
                label="+8%" 
                size="small" 
                color="success"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="warning.main" gutterBottom>
                4.7/5
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Average Rating
              </Typography>
              <Chip 
                label="+0.1" 
                size="small" 
                color="success"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search jobs..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Payment</InputLabel>
                <Select
                  value={filterPayment}
                  label="Payment"
                  onChange={(e) => setFilterPayment(e.target.value)}
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
              >
                More Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedJobs.length > 0 && selectedJobs.length < filteredJobs.length}
                    checked={filteredJobs.length > 0 && selectedJobs.length === filteredJobs.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Job Details</TableCell>
                <TableCell>Cleaner</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((job) => {
                  const isItemSelected = isSelected(job.id);
                  return (
                    <TableRow
                      key={job.id}
                      hover
                      selected={isItemSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, job.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {job.service}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {job.customer}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.address}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {job.cleaner.charAt(0)}
                          </Avatar>
                          <Typography variant="body2">{job.cleaner}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.date.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.duration}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.duration}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="600" color="primary">
                          ${job.amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={job.status} />
                      </TableCell>
                      <TableCell>
                        <PaymentChip status={job.paymentStatus} />
                      </TableCell>
                      <TableCell>
                        {job.rating ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating value={job.rating} size="small" readOnly />
                            <Typography variant="body2">
                              {job.rating}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No rating
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionMenuOpen(e, job)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredJobs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleActionMenuClose}>
          <ReceiptIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedJob?.review) {
              handleViewReview(selectedJob);
            }
            handleActionMenuClose();
          }}
          disabled={!selectedJob?.review}
        >
          <StarIcon sx={{ mr: 1 }} />
          View Review
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Job
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedJob && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon color="warning" />
                Customer Review
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Service</Typography>
                  <Typography fontWeight="600">{selectedJob.service}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Customer</Typography>
                  <Typography fontWeight="600">{selectedJob.customer}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Cleaner</Typography>
                  <Typography fontWeight="600">{selectedJob.cleaner}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Rating</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={selectedJob.rating} readOnly />
                    <Typography fontWeight="600">{selectedJob.rating}/5</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Review
                  </Typography>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography>{selectedJob.review}</Typography>
                  </Card>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReviewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}